import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import { Reveal } from "@/components/motion";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { ArrowUpRight } from "lucide-react";

const QA = [
  {
    id: "telemetry",
    q: "How is telemetry acquired?",
    a: "From the devices and apps your fleet already runs. At each delivery event, the driver's device — or your dispatch system — sends coordinates, speed, heading, GPS accuracy, timestamps, and proof photos through the ingest API. GeoVerify is routing-agnostic: any TMS, dispatch platform, or fleet app that can make an HTTP call can send events. No hardware to install, no tools to replace.",
  },
  {
    id: "spoofing",
    q: "How hard is it to spoof?",
    a: "Faking one field is easy — which is exactly why GeoVerify cross-checks every field against every other field. Reported speed must match the distance actually covered between fixes. The device heading must match the direction of movement. The photo's timestamp must match the event's timestamp. Ten physics and consistency checks run on every event, and a fabricated event has to stay consistent across all of them at once. And we'll tell you honestly: a perfectly self-consistent fake can pass a single isolated event. The structural answer is continuous telemetry streaming — on our roadmap — where a faker must invent an entire believable journey, not one plausible moment.",
  },
  {
    id: "volume",
    q: "How many events per day can the system process?",
    a: "The engines are deterministic math — no AI inference sits in the verification path — so a single event evaluates in milliseconds. Up to 100 events can be verified in one batch call, and the architecture scales horizontally: more volume means more compute, not more cleverness. Pilots start with 10,000 included events; production tiers are sized per network.",
  },
  {
    id: "why-not-tms",
    q: "Why can't our existing TMS vendor just add this?",
    a: "Conflict of interest. A TMS grading its own deliveries is the referee playing for one team — when a dispute reaches an insurer or a courtroom, the platform that profits from 'delivered' verdicts is the wrong witness. GeoVerify's value is neutrality: we don't sell routing, dispatch, or fleet software, so our verdicts are credible to the other side of the table. And because we sit above every system, one evidence standard applies across all your carriers and platforms — not just the ones on a single vendor.",
  },
  {
    id: "legal",
    q: "Will the audit trail actually hold up in a dispute?",
    a: "Every verification is sealed into an append-only log where each record's SHA-256 signature covers the record before it — altering any historical record breaks every signature that follows. Audit exports are signed, self-contained packets with a built-in chain-validity check that an insurer's own systems can verify independently. Honest framing: this is tamper-evidence, not a court ruling. What it proves, mathematically, is that the record you're holding is the record written at the time — and that nothing since was edited, reordered, or deleted.",
  },
];

const Faq = () => (
  <div data-testid="faq-page" className="bg-background text-foreground min-h-screen">
    <Nav />
    <main className="pt-16">
      <section className="border-b border-border grain-overlay relative">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-24 lg:py-32">
          <Reveal>
            <p className="font-mono text-xs tracking-[0.25em] uppercase text-telemetry mb-6">
              Straight Answers
            </p>
            <h1 className="font-display font-black tracking-tight text-4xl sm:text-5xl lg:text-6xl leading-[0.95] max-w-3xl">
              Five questions every serious buyer asks.
            </h1>
            <p className="mt-6 text-base md:text-lg text-muted-foreground leading-relaxed max-w-2xl">
              The same questions investors, insurers, and operations leads put to us — answered the
              way we&apos;d answer them across a table. No marketing adjectives.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="border-b border-border">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-16 lg:py-24 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-3 hidden lg:block">
            <p className="font-mono text-[10px] tracking-[0.25em] uppercase text-muted-foreground">
              {QA.length} QUESTIONS · 0 ADJECTIVES
            </p>
          </div>
          <div className="lg:col-span-9">
            <Accordion type="single" collapsible className="border border-border">
              {QA.map((item, i) => (
                <AccordionItem
                  key={item.id}
                  value={item.id}
                  className="border-b border-border last:border-b-0 px-6 lg:px-8"
                >
                  <AccordionTrigger
                    data-testid={`faq-question-${item.id}`}
                    className="hover:no-underline py-6 group"
                  >
                    <span className="flex items-baseline gap-5 text-left">
                      <span className="font-mono text-xs text-muted-foreground shrink-0">
                        0{i + 1}
                      </span>
                      <span className="font-display font-bold tracking-tight text-lg lg:text-xl group-hover:text-telemetry transition-colors duration-200">
                        {item.q}
                      </span>
                    </span>
                  </AccordionTrigger>
                  <AccordionContent data-testid={`faq-answer-${item.id}`} className="pb-6">
                    <p className="text-sm lg:text-base leading-relaxed text-muted-foreground max-w-3xl pl-0 lg:pl-11">
                      {item.a}
                    </p>
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      <section className="border-b border-border">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-20 lg:py-24 flex flex-wrap items-center justify-between gap-8">
          <Reveal>
            <h2 className="font-display font-extrabold tracking-tight text-3xl lg:text-4xl max-w-xl">
              Don&apos;t take our word for it. Run the engine.
            </h2>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="flex flex-wrap gap-4">
              <a
                data-testid="faq-playground-btn"
                href="/playground"
                className="bg-foreground text-background font-mono text-xs tracking-[0.2em] uppercase px-8 py-4 hover:bg-telemetry transition-colors duration-200 flex items-center gap-2"
              >
                Open Playground <ArrowUpRight className="w-4 h-4" />
              </a>
              <a
                data-testid="faq-access-btn"
                href="/#request-access"
                className="border border-foreground font-mono text-xs tracking-[0.2em] uppercase px-8 py-4 hover:bg-foreground hover:text-background transition-colors duration-200 flex items-center gap-2"
              >
                Request Access
              </a>
            </div>
          </Reveal>
        </div>
      </section>
    </main>
    <Footer />
  </div>
);

export default Faq;
